import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Sparkles } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { Reveal } from "./motion/Reveal";

const CASES = [
  {
    id: "riyadh-retail",
    tag: { en: "Retail & E-commerce · 42 stores", ar: "التجزئة والتجارة الإلكترونية · 42 فرعًا" },
    title: {
      en: "Anonymised: A Riyadh retail group unlocked SAR 18M of trapped working capital in one quarter.",
      ar: "شركة تجزئة في الرياض حرّرت 18 مليون ريال من رأس المال العامل المحتجز خلال ربع واحد.",
    },
    problem: {
      en: "Ninety-day inventory sitting in three warehouses, DSO drifting past 74 days, and a founder-led buying function with no margin discipline.",
      ar: "مخزون متجمّد لتسعين يومًا في ثلاثة مستودعات، متوسط تحصيل يتجاوز 74 يومًا، ووظيفة شراء بلا انضباط في هوامش الربح.",
    },
    metrics: [
      { label: { en: "DSO", ar: "أيام التحصيل" }, before: "74", after: "39", down: true },
      { label: { en: "Gross margin", ar: "الهامش الإجمالي" }, before: "22%", after: "31%", down: false },
      { label: { en: "Working capital", ar: "رأس المال العامل" }, before: "—", after: "+18M SAR", down: false },
    ],
  },
  {
    id: "eastern-manufacturing",
    tag: { en: "Manufacturing · Eastern Province", ar: "الصناعة · المنطقة الشرقية" },
    title: {
      en: "Anonymised: A precision-parts manufacturer turned a SAR 3.2M annual loss into a 14% EBITDA margin in nine months.",
      ar: "مصنع قطع دقيقة حوّل خسارة سنوية بلغت 3.2 مليون ريال إلى هامش EBITDA بنسبة 14٪ خلال تسعة أشهر.",
    },
    problem: {
      en: "Cost-plus quoting on obsolete BOMs, unaudited scrap, and a Zakat exposure the CFO had not yet quantified.",
      ar: "تسعير قائم على تكلفة بائدة، هالك غير مدقّق، وتعرّض زكوي لم تحدّده الإدارة المالية بعد.",
    },
    metrics: [
      { label: { en: "EBITDA margin", ar: "هامش EBITDA" }, before: "-6%", after: "+14%", down: false },
      { label: { en: "Scrap rate", ar: "معدل الهالك" }, before: "9.1%", after: "3.4%", down: true },
      { label: { en: "Zakat provision", ar: "مخصص الزكاة" }, before: "—", after: "Fully reconciled", down: false },
    ],
  },
  {
    id: "jeddah-hospitality",
    tag: { en: "Hospitality · 2 boutique hotels", ar: "الضيافة · فندقان بوتيك" },
    title: {
      en: "Anonymised: A boutique hospitality group financed expansion #3 without a single new SAR of shareholder equity.",
      ar: "مجموعة ضيافة بوتيك موّلت التوسّع الثالث دون ريال واحد جديد من حقوق الملاّك.",
    },
    problem: {
      en: "Property-by-property P&L, no group-level FP&A, and a Ramadan-heavy cash cycle no lender would underwrite.",
      ar: "حسابات ربح وخسارة لكل فندق على حدة، بلا تخطيط مالي مجموعي، ودورة نقدية شديدة الاعتماد على رمضان يصعب على الممولين قبولها.",
    },
    metrics: [
      { label: { en: "Occupancy (RevPAR)", ar: "الإشغال (RevPAR)" }, before: "SAR 412", after: "SAR 587", down: false },
      { label: { en: "Debt-service cover", ar: "تغطية خدمة الدين" }, before: "0.9x", after: "1.8x", down: false },
      { label: { en: "New equity needed", ar: "حقوق ملاك جديدة" }, before: "—", after: "SAR 0", down: false },
    ],
  },
];


export default function CaseStudies() {
  const { t, lang } = useLang();
  const heading = lang === "ar" ? "قصص تحوّل" : "Turnaround stories";
  const kicker = lang === "ar" ? "دراسات حالة مجهّلة الهوية" : "Anonymised case studies";
  const foot = lang === "ar"
    ? "الأسماء مخفية بموجب اتفاقيات السرية. الأرقام حقيقية، خضعت للمراجعة الداخلية."
    : "Names withheld under NDA. Numbers real, internally reviewed.";

  return (
    <section id="cases" className="py-24 sm:py-32 bg-cb-cream relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 relative">
        <Reveal>
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.28em] text-cb-emerald mb-4 flex items-center gap-2">
              <span className="w-8 h-px bg-cb-gold"></span>
              {kicker}
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl text-cb-primary leading-[1.05]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {heading}
              <span className="italic text-cb-gold">.</span>
            </h2>
          </div>
        </Reveal>

        <div className="space-y-16">
          {CASES.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05}>
              <article className="grid md:grid-cols-12 gap-8 border-t border-cb-primary/10 pt-10">
                {/* Chapter numeral */}
                <div className="md:col-span-2">
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-6xl md:text-7xl text-cb-gold italic font-light leading-none"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    0{i + 1}
                  </motion.div>
                  <div className="mt-3 text-[11px] uppercase tracking-widest text-cb-secondary" data-testid={`case-tag-${c.id}`}>
                    {c.tag[lang]}
                  </div>
                </div>

                {/* Story */}
                <div className="md:col-span-6">
                  <h3 className="text-xl sm:text-2xl text-cb-primary leading-snug" style={{ fontFamily: "'Playfair Display', serif" }} data-testid={`case-title-${c.id}`}>
                    {c.title[lang]}
                  </h3>
                  <p className="mt-5 text-cb-secondary leading-relaxed">
                    {c.problem[lang]}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cb-emerald">
                    <Sparkles size={14} /> {lang === "ar" ? "بعد ٩٠–٢٧٠ يومًا مع كابيتال باي" : "After 90–270 days with Capital Bay"}
                  </div>
                </div>

                {/* Metrics */}
                <div className="md:col-span-4 space-y-4">
                  {c.metrics.map((m, mi) => (
                    <div key={mi} className="border border-cb-primary/10 rounded-2xl p-4 bg-white/60 backdrop-blur">
                      <div className="text-[11px] uppercase tracking-widest text-cb-secondary">{m.label[lang]}</div>
                      <div className="mt-2 flex items-baseline gap-3">
                        <span className="text-cb-secondary line-through text-sm" dir="ltr">{m.before}</span>
                        <span className="text-cb-primary text-xl font-semibold" style={{ fontFamily: "Outfit" }} dir="ltr">{m.after}</span>
                        <span className={`ms-auto inline-flex items-center gap-1 text-xs ${m.down ? "text-cb-emerald" : "text-cb-emerald"}`}>
                          {m.down ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 text-xs uppercase tracking-widest text-cb-secondary text-center">
          {foot}
        </div>
      </div>
    </section>
  );
}
